<div class="flex flex-col gap-4">
    <p class="text-xs text-muted-color">
        Art falls back to the game template unless overridden here. Pick a
        finalized variant, generate, or upload — it applies to this realm
        immediately.
    </p>

    <!-- ── Current icon + banner ──────────────────────────────────────────── -->
    <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
                <label class="font-medium">Icon &amp; Banner</label>
                @if (iconOverridden() || bannerOverridden()) {
                <p-tag
                    severity="warn"
                    value="Realm override"
                    [rounded]="true"
                />
                } @else {
                <span class="text-xs text-muted-color italic"
                    >From template</span
                >
                }
            </div>
            @if (canManage()) {
            <div class="flex items-center gap-1 flex-wrap">
                <p-button
                    label="Generate with AI"
                    icon="pi pi-microchip-ai"
                    size="small"
                    severity="secondary"
                    text
                    (onClick)="openAiArt()"
                />
                <p-button
                    label="Upload icon"
                    icon="pi pi-upload"
                    size="small"
                    severity="secondary"
                    text
                    [loading]="iconUploading()"
                    (onClick)="iconFile.click()"
                />
                <input
                    #iconFile
                    type="file"
                    accept="image/*"
                    class="hidden"
                    (change)="uploadIcon($event.target)"
                />
                <p-button
                    label="Upload banner"
                    icon="pi pi-upload"
                    size="small"
                    severity="secondary"
                    text
                    [loading]="bannerUploading()"
                    (onClick)="bannerFile.click()"
                />
                <input
                    #bannerFile
                    type="file"
                    accept="image/*"
                    class="hidden"
                    (change)="uploadBanner($event.target)"
                />
                @if (iconOverridden() || bannerOverridden()) {
                <p-button
                    label="Use template"
                    icon="pi pi-undo"
                    size="small"
                    severity="secondary"
                    text
                    (onClick)="inheritIconBanner()"
                />
                }
            </div>
            }
        </div>
        <div class="flex items-start gap-3">
            <div
                class="w-20 h-20 flex-shrink-0 rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center overflow-hidden bg-surface-50 dark:bg-surface-800"
            >
                @if (effIcon()) {
                <img
                    [src]="resolve(effIcon())"
                    alt="Icon"
                    class="w-full h-full object-cover cursor-pointer"
                    title="View large"
                    (click)="preview.open(resolve(effIcon()), 'Icon')"
                />
                } @else {
                <i class="pi pi-image text-muted-color text-xl"></i>
                }
            </div>
            <div
                class="aspect-video w-56 rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center overflow-hidden bg-surface-50 dark:bg-surface-800"
            >
                @if (effBanner()) {
                <img
                    [src]="resolve(effBanner())"
                    alt="Banner"
                    class="w-full h-full object-cover cursor-pointer"
                    title="View large"
                    (click)="preview.open(resolve(effBanner()), 'Banner')"
                />
                } @else {
                <i class="pi pi-image text-muted-color text-xl"></i>
                }
            </div>
        </div>
    </div>

    <!-- ── Current images ─────────────────────────────────────────────────── -->
    <div
        class="flex flex-col gap-2 pt-3 border-t border-surface-200 dark:border-surface-700"
    >
        <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
                <label class="font-medium"
                    >Images
                    <span class="text-xs text-muted-color font-normal"
                        >(portrait)</span
                    ></label
                >
                @if (imagesOverridden()) {
                <p-tag
                    severity="warn"
                    value="Realm override"
                    [rounded]="true"
                />
                } @else {
                <span class="text-xs text-muted-color italic"
                    >From template</span
                >
                }
            </div>
            @if (canManage()) {
            <div class="flex items-center gap-1 flex-wrap">
                <p-button
                    label="Generate with AI"
                    icon="pi pi-microchip-ai"
                    size="small"
                    severity="secondary"
                    text
                    (onClick)="openAiPages()"
                />
                <p-button
                    label="Upload image"
                    icon="pi pi-upload"
                    size="small"
                    severity="secondary"
                    text
                    [loading]="imageUploading()"
                    (onClick)="imageFile.click()"
                />
                <input
                    #imageFile
                    type="file"
                    accept="image/*"
                    class="hidden"
                    (change)="uploadImage($event.target)"
                />
                @if (imagesOverridden()) {
                <p-button
                    label="Use template"
                    icon="pi pi-undo"
                    size="small"
                    severity="secondary"
                    text
                    (onClick)="inheritImages()"
                />
                }
            </div>
            }
        </div>
        @if (effImages().length > 0) {
        <div class="flex flex-wrap gap-2">
            @for (img of effImages(); track img) {
            <div class="relative">
                <img
                    [src]="resolve(img)"
                    alt="Image"
                    class="w-16 h-28 rounded border border-surface-200 dark:border-surface-700 object-cover bg-surface-50 dark:bg-surface-800 cursor-pointer"
                    title="View large"
                    (click)="preview.open(resolve(img), 'Image')"
                />
                @if (canManage() && imagesOverridden()) {
                <i
                    class="pi pi-times absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 text-xs cursor-pointer"
                    title="Remove"
                    (click)="removeImage(img); $event.stopPropagation()"
                ></i>
                }
            </div>
            }
        </div>
        } @else {
        <span class="text-sm text-muted-color italic">No images.</span>
        }
    </div>

    <!-- ── Saved variants ─────────────────────────────────────────────────── -->
    <div
        class="flex flex-col gap-3 pt-3 border-t border-surface-200 dark:border-surface-700"
    >
        <label class="font-medium">Saved variants</label>
        @if (loading()) {
        <div class="flex items-center gap-2 text-muted-color text-sm py-3">
            <i class="pi pi-spin pi-spinner"></i><span>Loading variants…</span>
        </div>
        } @else { @if (iconBannerViews().length === 0 && pagesViews().length ===
        0) {
        <span class="text-sm text-muted-color italic"
            >No variants yet — generate or upload above.</span
        >
        }

        <!-- Icon & Banner variants -->
        @if (iconBannerViews().length > 0) {
        <div class="text-xs font-medium text-muted-color">Icon &amp; Banner</div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            @for (v of iconBannerViews(); track v.art.Id) {
            <div
                class="flex flex-col gap-2 rounded border p-3"
                [class.border-primary]="v.applied"
                [class.border-surface-200]="!v.applied"
                [class.dark:border-surface-700]="!v.applied"
            >
                <div class="flex items-center justify-between gap-2">
                    <span class="font-medium truncate" [title]="v.name">{{
                        v.name
                    }}</span>
                    @if (v.applied) {
                    <p-tag severity="success" value="Applied" />
                    }
                </div>
                <div
                    class="w-full aspect-video rounded border border-surface-200 dark:border-surface-700 overflow-hidden flex items-center justify-center bg-surface-50 dark:bg-surface-800"
                >
                    @if (v.bannerPreview) {
                    <img
                        [src]="v.bannerPreview"
                        alt="Banner"
                        class="w-full h-full object-contain cursor-pointer"
                        title="View large"
                        (click)="preview.open(v.bannerPreview, v.name)"
                    />
                    } @else {
                    <i class="pi pi-image text-muted-color"></i>
                    }
                </div>
                <div class="flex items-center justify-between gap-2">
                    <div
                        class="w-12 h-12 shrink-0 rounded border border-surface-200 dark:border-surface-700 overflow-hidden flex items-center justify-center bg-surface-50 dark:bg-surface-800"
                    >
                        @if (v.iconPreview) {
                        <img
                            [src]="v.iconPreview"
                            alt="Icon"
                            class="w-full h-full object-cover cursor-pointer"
                            title="View large"
                            (click)="preview.open(v.iconPreview, v.name)"
                        />
                        } @else {
                        <i class="pi pi-image text-muted-color"></i>
                        }
                    </div>
                    <div class="flex items-center gap-2">
                        <p-button
                            [label]="v.applied ? 'Applied' : 'Select'"
                            icon="pi pi-check"
                            size="small"
                            [outlined]="!v.applied"
                            [disabled]="v.applied || busyId() !== null"
                            [loading]="busyId() === v.art.Id"
                            (onClick)="select(v)"
                        />
                        @if (canManage()) {
                        <p-button
                            icon="pi pi-trash"
                            size="small"
                            severity="danger"
                            text
                            pTooltip="Delete variant"
                            [disabled]="busyId() !== null"
                            (onClick)="askDelete(v)"
                        />
                        }
                    </div>
                </div>
            </div>
            }
        </div>
        }

        <!-- Page variants -->
        @if (pagesViews().length > 0) {
        <div class="text-xs font-medium text-muted-color mt-2">Pages</div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            @for (v of pagesViews(); track v.art.Id) {
            <div
                class="flex flex-col gap-2 rounded border p-3"
                [class.border-primary]="v.applied"
                [class.border-surface-200]="!v.applied"
                [class.dark:border-surface-700]="!v.applied"
            >
                <div class="flex items-center justify-between gap-2">
                    <span class="font-medium truncate" [title]="v.name">{{
                        v.name
                    }}</span>
                    @if (v.applied) {
                    <p-tag severity="success" value="Applied" />
                    }
                </div>
                <div class="flex gap-2 overflow-x-auto">
                    @for (p of v.pagePreviews; track $index) {
                    <img
                        [src]="p"
                        [alt]="'Page ' + ($index + 1)"
                        class="w-12 h-20 shrink-0 rounded border border-surface-200 dark:border-surface-700 object-cover bg-surface-50 dark:bg-surface-800 cursor-pointer"
                        title="View large"
                        (click)="preview.open(p, v.name)"
                    />
                    }
                </div>
                <div class="flex items-center justify-end gap-2">
                    <p-button
                        [label]="v.applied ? 'Applied' : 'Select'"
                        icon="pi pi-check"
                        size="small"
                        [outlined]="!v.applied"
                        [disabled]="v.applied || busyId() !== null"
                        [loading]="busyId() === v.art.Id"
                        (onClick)="select(v)"
                    />
                    @if (canManage()) {
                    <p-button
                        icon="pi pi-trash"
                        size="small"
                        severity="danger"
                        text
                        pTooltip="Delete variant"
                        [disabled]="busyId() !== null"
                        (onClick)="askDelete(v)"
                    />
                    }
                </div>
            </div>
            }
        </div>
        } }
    </div>
</div>

<!-- Delete confirm -->
<p-dialog
    header="Delete variant"
    [visible]="deleteVisible()"
    (visibleChange)="deleteVisible.set($event)"
    [modal]="true"
    [draggable]="false"
    appendTo="body"
    [style]="{ width: '26rem' }"
>
    <p class="text-sm">
        Delete the variant “{{ deleteTarget()?.name }}”? This removes it for
        every realm sharing this site template.
    </p>
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button
                label="Cancel"
                severity="secondary"
                outlined
                [disabled]="deleting()"
                (onClick)="deleteVisible.set(false)"
            />
            <p-button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                [loading]="deleting()"
                (onClick)="confirmDelete()"
            />
        </div>
    </ng-template>
</p-dialog>

<app-ai-finalize-game-art
    [visible]="aiArtVisible()"
    (visibleChange)="aiArtVisible.set($event)"
    [template]="gameTemplate()"
    [fixedSiteTemplateId]="siteTemplateId()"
    [realmId]="adminGame()?.RealmId ?? null"
    [titleOverride]="titleOverride()"
    (applied)="onAiArtApplied($event)"
/>

<app-ai-pages-generate
    [visible]="aiPagesVisible()"
    (visibleChange)="aiPagesVisible.set($event)"
    [template]="gameTemplate()"
    [fixedSiteTemplateId]="siteTemplateId()"
    [realmId]="adminGame()?.RealmId ?? null"
    [titleOverride]="titleOverride()"
    (applied)="onAiPagesApplied($event)"
/>
